package com.karyadina.ui.screens.flashcards.components

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.TrendingUp
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.material3.ripple
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.karyadina.model.FlashcardStats
import com.karyadina.ui.theme.FlashcardColors
import com.karyadina.ui.theme.FlashcardIcons
import com.karyadina.ui.theme.ElevationProfile
import com.karyadina.ui.theme.ElevationUtils.rippleEffect

@Composable
fun MasteryChip(label: String, count: Int, color: Color) {
    val isDarkMode = isSystemInDarkTheme()
    val lightAlpha = if (isDarkMode) 0.25f else 0.15f
    val darkAlpha = if (isDarkMode) 0.12f else 0.05f
    
    val chipGradient = Brush.linearGradient(
        colors = listOf(
            color.copy(alpha = lightAlpha),
            color.copy(alpha = darkAlpha)
        )
    )
    
    Surface(
        shape = RoundedCornerShape(16.dp),
        color = Color.Transparent,
        modifier = Modifier
            .padding(2.dp)
            .clip(RoundedCornerShape(16.dp))
            .background(chipGradient),
        border = androidx.compose.foundation.BorderStroke(1.dp, color.copy(alpha = if (isDarkMode) 0.5f else 0.3f))
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            AnimatedCounter(count)
            Text(
                label,
                style = MaterialTheme.typography.labelSmall,
                color = color.copy(alpha = 0.8f)
            )
        }
    }
}

@Composable
fun OverallStatsCard(stats: FlashcardStats) {
    // Create gradient background based on mastery progress
    val masteryPercentage = if (stats.totalCards > 0) {
        (stats.masteredCards.toFloat() / stats.totalCards) * 100
    } else {
        0f
    }
    
    val isDarkMode = isSystemInDarkTheme()
    val lightAlphaCard = if (isDarkMode) 0.25f else 0.15f
    val darkAlphaCard = if (isDarkMode) 0.12f else 0.05f
    val lightAlphaStat = if (isDarkMode) 0.18f else 0.1f
    val darkAlphaStat = if (isDarkMode) 0.1f else 0.05f
    
    val cardGradient = when {
        masteryPercentage >= 75 -> Brush.linearGradient(
            colors = listOf(
                FlashcardColors.getMasteryColor("MASTERED").copy(alpha = lightAlphaCard),
                FlashcardColors.getMasteryColor("MASTERED").copy(alpha = darkAlphaCard)
            )
        )
        masteryPercentage >= 50 -> Brush.linearGradient(
            colors = listOf(
                FlashcardColors.getMasteryColor("REVIEW").copy(alpha = lightAlphaCard),
                FlashcardColors.getMasteryColor("REVIEW").copy(alpha = darkAlphaCard)
            )
        )
        else -> Brush.linearGradient(
            colors = listOf(
                MaterialTheme.colorScheme.primaryContainer.copy(alpha = lightAlphaCard),
                MaterialTheme.colorScheme.primaryContainer.copy(alpha = darkAlphaCard)
            )
        )
    }
    
    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(cardGradient)
            .rippleEffect(bounded = true),
        colors = CardDefaults.elevatedCardColors(
            containerColor = Color.Transparent
        ),
        elevation = CardDefaults.elevatedCardElevation(
            defaultElevation = ElevationProfile.raisedElevation
        )
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    "Overall Progress",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Icon(Icons.Filled.Analytics, contentDescription = null)
            }
            
            Spacer(modifier = Modifier.height(16.dp))
            
            // Create gradients for stat items
            val subjectGradient = Brush.linearGradient(
                colors = listOf(
                    MaterialTheme.colorScheme.primary.copy(alpha = lightAlphaStat),
                    MaterialTheme.colorScheme.primary.copy(alpha = darkAlphaStat)
                )
            )
            val cardGradientStat = Brush.linearGradient(
                colors = listOf(
                    FlashcardColors.getMasteryColor("REVIEW").copy(alpha = lightAlphaStat),
                    FlashcardColors.getMasteryColor("REVIEW").copy(alpha = darkAlphaStat)
                )
            )
            val masteredGradient = Brush.linearGradient(
                colors = listOf(
                    FlashcardColors.getCorrectAnswerColor().copy(alpha = lightAlphaStat),
                    FlashcardColors.getCorrectAnswerColor().copy(alpha = darkAlphaStat)
                )
            )
            
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceEvenly
            ) {
                StatItem("Subjects", stats.totalSubjects.toString(), Icons.Filled.Book, subjectGradient)
                StatItem("Cards", stats.totalCards.toString(), Icons.Filled.Style, cardGradientStat)
                StatItem("Mastered", stats.masteredCards.toString(), Icons.Filled.CheckCircle, masteredGradient)
            }
            
            if (stats.totalReviewSessions > 0) {
                Spacer(modifier = Modifier.height(12.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceEvenly
                ) {
                    StatItem("Sessions", stats.totalReviewSessions.toString(), Icons.Filled.History, subjectGradient)
                    StatItem("Accuracy", "${stats.averageAccuracy.toInt()}%", Icons.AutoMirrored.Filled.TrendingUp, cardGradientStat)
                    StatItem("Due", stats.cardsToReview.toString(), Icons.Filled.Schedule, masteredGradient)
                }
            }
            
            // Learning Progress Explanation
            if (stats.totalCards > 0) {
                Spacer(modifier = Modifier.height(16.dp))
                HorizontalDivider()
                Spacer(modifier = Modifier.height(12.dp))
                
                val masteryPercentInt = masteryPercentage.toInt()
                
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(
                        "📚 How Mastery Works",
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        "• Review cards correctly 5 times to master them",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                    )
                    Text(
                        "• Time between reviews increases as you progress",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                    )
                    Text(
                        "• Mastered cards review every 30 days",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.8f)
                    )
                    
                    if (masteryPercentInt < 25) {
                        Spacer(Modifier.height(4.dp))
                        Text(
                            "💪 Keep going! You're building a strong foundation.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.primary,
                            fontWeight = FontWeight.Medium
                        )
                    } else if (masteryPercentInt < 75) {
                        Spacer(Modifier.height(4.dp))
                        Text(
                            "🚀 Great progress! Stay consistent with reviews.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.primary,
                            fontWeight = FontWeight.Medium
                        )
                    } else {
                        Spacer(Modifier.height(4.dp))
                        Text(
                            "🏆 Amazing! You're almost there!",
                            style = MaterialTheme.typography.bodySmall,
                            color = FlashcardColors.getCorrectAnswerColor(),
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun AnimatedCounter(
    value: Int,
    durationMs: Int = 800
) {
    var displayValue by remember { mutableStateOf(0) }
    val animationProgress by animateFloatAsState(
        targetValue = 1f,
        animationSpec = tween(durationMillis = durationMs),
        label = "counter"
    )
    
    LaunchedEffect(value) {
        displayValue = (value * animationProgress).toInt()
    }
    
    Text(
        displayValue.toString(),
        style = MaterialTheme.typography.titleLarge,
        fontWeight = FontWeight.Bold
    )
}

@Composable
fun StatItem(
    label: String,
    value: String,
    icon: ImageVector,
    gradient: Brush? = null
) {
    val isNumeric = value.all { it.isDigit() || it == '%' }
    val numericValue = value.filter { it.isDigit() }.toIntOrNull() ?: 0
    
    Surface(
        shape = RoundedCornerShape(12.dp),
        modifier = Modifier
            .clip(RoundedCornerShape(12.dp))
            .then(
                if (gradient != null) {
                    Modifier.background(gradient)
                } else {
                    Modifier.background(MaterialTheme.colorScheme.surfaceVariant)
                }
            ),
        color = Color.Transparent
    ) {
        Column(
            modifier = Modifier
                .padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Icon(
                icon,
                contentDescription = null,
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.primary
            )
            Spacer(modifier = Modifier.height(6.dp))
            if (isNumeric) {
                AnimatedCounter(numericValue)
            } else {
                Text(
                    value,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
            }
            Text(
                label,
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f)
            )
        }
    }
}

